import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { ApiClient } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useQuery, type QueryState } from '../../useQuery';

/** Load one route for the Overview with the session's client. */
export function useApi<T>(load: (client: ApiClient, signal: AbortSignal) => Promise<T>, deps: unknown[] = [], enabled = true): QueryState<T> {
  const { client, session } = useSession();
  return useQuery<T>((signal) => load(client, signal), [client, session?.token, ...deps], { enabled });
}

/** A block heading: the name, then its meta line (design: 13px 600 + 11.5px muted). */
export function BlockHead({ title, meta, children, small }: { title: string; meta?: ReactNode; children?: ReactNode; small?: boolean }) {
  return (
    <div className="ov-head">
      <h3 className={`ov-head__title${small ? ' ov-head__title--small' : ''}`}>{title}</h3>
      {meta !== undefined && <span className="ov-head__meta">{meta}</span>}
      {children}
    </div>
  );
}

/** The uppercase caption the design uses inside Guard posture (OUTCOMES, ACTION LAYER). */
export function Caption({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="ov-caption">
      <span>{children}</span>
      {right !== undefined && <span className="ov-caption__right">{right}</span>}
    </div>
  );
}

/** An in-page link in the design's teal ("See all 9 facilities", "Guardian"). */
export function MoreLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="ov-more">
      {children}
    </Link>
  );
}

/** Shorten "The Arbour at Westminster Manor" to "Arbour at Westminster Manor", as the design does. */
export function shortName(n: string): string {
  return n.replace(/^The /, '');
}

/** Two-letter initials for an owner chip. */
export function initials(name: string | null | undefined): string {
  if (!name) return '';
  return name
    .replace(/[^A-Za-z. ]/g, '')
    .split(/[ .]+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** Data-or-null from a query, for blocks that combine several sources. */
export function dataOf<T>(q: QueryState<T>): T | null {
  return q.data;
}
